// app/stores/useUiStore.ts
import { defineStore } from 'pinia'
import { ref } from 'vue'

export interface Alerta {
  id: string | number
  tipo?: string | null
  icone?: string
  titulo: string
  descricao: string
  inicio?: number | null  // Timestamp em ms
  termino?: number | null // Timestamp em ms
  fechavel?: boolean
}

export const useUiStore = defineStore('ui', () => {
  const sesskey = ref('')                         // sesskey do Moodle
  const openSlideoverMainMenu = ref(false)          // ESTADO: Slideover do Menu Principal (Mobile)

  const isAuthModalOpen = ref(false)               // Modal de Autenticação
  const openAuthModal = () => isAuthModalOpen.value = true
  const closeAuthModal = () => isAuthModalOpen.value = false

  const isForgotModalOpen = ref(false)                 // ESTADO: Modal de Recuperação da Senha
  const openForgotModal = () => isForgotModalOpen.value = true
  const closeForgotModal = () => isForgotModalOpen.value = false

  // ==========================================
  // ESTADO: Alertas Globais
  // ==========================================
  const alertsData = ref<Alerta[]>([])

  // Guardamos os IDs dos alertas que o usuário fechou manualmente
  // (Isso será salvo no LocalStorage pelo plugin persist)
  const dismissedAlertIds = ref<(string|number)[]>([])

  // Função para carregar os alertas do Backend
  const setSystemAlerts = (alerts: Alerta[]) => {
    alertsData.value = alerts
  }

  // Função para adicionar um alerta dinâmico localmente (do próprio Nuxt)
  const adicionarAlerta = (alerta: Alerta) => {
    alertsData.value.push(alerta)
  }

  // O usuário fechou o alerta clicando no 'X'
  const dispensarAlerta = (id: string | number) => {
    if (!dismissedAlertIds.value.includes(id)) {
      dismissedAlertIds.value.push(id)
    }
  }

  // Transforma as cores padrão do Moodle nas variantes do nosso Design System
  const mapColor = (colorName?: string | null): string => {
    const colorMap: Record<string, string> = {
      gray: 'neutral',
      green: 'success',
      blue: 'info',
      yellow: 'warning',
      red: 'error'
    }
    // Se a cor existir no mapa, retorna a tradução. Senão, retorna 'neutral' como fallback.
    return colorName && colorMap[colorName] ? colorMap[colorName] : 'neutral'
  }

  // Transforma as cores do App nos respectivos ícones Phosphor
  const mapIcon = (appColor: string): string => {
    const iconMap: Record<string, string> = {
      neutral: 'ph:chat-circle',
      success: 'ph:check-circle',
      info: 'ph:info',
      warning: 'ph:warning-circle',
      error: 'ph:cloud-x'
    }
    return iconMap[appColor] || 'ph:info' // Retorna 'ph:info' como fallback de segurança
  }

  // COMPUTED: Filtra os alertas e já traduz as cores
  const activeAlerts = computed(() => {
    const agora = Date.now()

    return alertsData.value
      .filter(alerta => {
        // 1. Já foi fechado pelo usuário?
        if (dismissedAlertIds.value.includes(alerta.id)) return false

        // 2. Data de início estipulada já chegou?
        if (alerta.inicio && agora < alerta.inicio) return false

        // 3. Data de término estipulada já passou?
        if (alerta.termino && agora > alerta.termino) return false

        return true
      })
      .map(alerta => {
        // Guarda a cor traduzida para usarmos nela mesma e no ícone
        const corApp = mapColor(alerta.tipo)

        return {
          ...alerta,
          tipo: corApp,
          icone: mapIcon(corApp)
        }
      })
  })

  return {
    sesskey,
    openSlideoverMainMenu,
    isAuthModalOpen, openAuthModal, closeAuthModal,
    isForgotModalOpen, openForgotModal, closeForgotModal,

    // Alertas
    alertsData,
    dismissedAlertIds,
    setSystemAlerts,
    adicionarAlerta,
    dispensarAlerta,
    activeAlerts
  }
}, {
  persist: {
    // Definimos explicitamente para salvar os alertas fechados, assim o usuário não vê de novo
    pick: ['dismissedAlertIds', 'sesskey']
  }
})